import { RiAddLine, RiCheckLine, RiExpandUpDownLine, RiShieldLine } from "@remixicon/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

interface UserOrg {
	id: string;
	name: string;
	slug?: string;
	logo?: string;
	role: string;
}

interface OrgSwitcherProps {
	orgs: UserOrg[];
	currentOrgId: string;
	userRole: string;
	isSuperadmin: boolean;
	onSwitchOrg: (orgId: string) => void;
	onCreateOrg: () => void;
}

function getOrgInitials(name: string): string {
	const [first, second] = name.split(" ").filter(Boolean);
	if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
	return name.slice(0, 2).toUpperCase();
}

export function OrgSwitcher({
	orgs,
	currentOrgId,
	userRole,
	isSuperadmin,
	onSwitchOrg,
	onCreateOrg,
}: OrgSwitcherProps) {
	const [open, setOpen] = useState(false);
	const currentOrg = orgs.find((o) => o.id === currentOrgId);

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu open={open} onOpenChange={setOpen}>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="size-8 rounded-lg">
								<AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-xs font-bold">
									{currentOrg ? getOrgInitials(currentOrg.name) : "P"}
								</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-semibold">{currentOrg?.name ?? "Organization"}</span>
								<span className="truncate text-xs text-muted-foreground capitalize">
									{userRole}
								</span>
							</div>
							<RiExpandUpDownLine className="ml-auto size-4" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent side="bottom" align="start" sideOffset={4}>
						<DropdownMenuGroup>
							<DropdownMenuLabel>Your Organizations ({orgs.length})</DropdownMenuLabel>
							{orgs.map((org) => (
								<DropdownMenuItem
									key={org.id}
									onClick={() => {
										if (org.id !== currentOrgId) {
											onSwitchOrg(org.id);
										}
										setOpen(false);
									}}
									className="gap-2"
								>
									<Avatar className="size-6 rounded-md">
										<AvatarFallback className="rounded-md text-[10px] font-bold">
											{getOrgInitials(org.name)}
										</AvatarFallback>
									</Avatar>
									<span className="flex-1 truncate">{org.name}</span>
									<Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">
										{org.role}
									</Badge>
									{org.id === currentOrgId && <RiCheckLine className="size-4 text-primary" />}
								</DropdownMenuItem>
							))}
						</DropdownMenuGroup>
						{isSuperadmin && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild onSelect={() => setOpen(false)}>
									<Link to="/admin">
										<RiShieldLine className="size-4" />
										Admin Panel
									</Link>
								</DropdownMenuItem>
							</>
						)}
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onClick={() => {
								onCreateOrg();
								setOpen(false);
							}}
						>
							<RiAddLine className="size-4" />
							Create Organization
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
